import React, { useState, useEffect, useMemo, useCallback } from "react";
import { collection, getDocs, getDoc, doc, updateDoc, serverTimestamp, runTransaction } from "firebase/firestore";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Line, Pie } from "react-chartjs-2";
import { db } from "../config/Firebase";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logoSrc from "../assets/images/icon.png";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

export function OrderingStatus() {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatingOrder, setUpdatingOrder] = useState(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("all");
  const [selectedDateFilter, setSelectedDateFilter] = useState("overall");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [searchBillNumber, setSearchBillNumber] = useState("");
  const [searchPatient, setSearchPatient] = useState("");
  const [userBranch, setUserBranch] = useState(null);

  // Fetch branch on component mount
  useEffect(() => {
    const fetchBranch = async () => {
      try {
        const username = localStorage.getItem('currentUsername');
        if (!username) {
          setUserBranch('ADMIN');
          return;
        }
        const ldbDoc = await getDoc(doc(db, 'LDB', username));
        if (ldbDoc.exists() && ldbDoc.data().branch) {
          setUserBranch(ldbDoc.data().branch);
          return;
        }
        const lubDoc = await getDoc(doc(db, 'LUB', username));
        if (lubDoc.exists() && lubDoc.data().branch) {
          setUserBranch(lubDoc.data().branch);
          return;
        }
        setUserBranch('ADMIN');
      } catch (error) {
        console.error('Error fetching branch:', error);
        setUserBranch('ADMIN');
      }
    };
    fetchBranch();
  }, []);
  
  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [deliveryDescription, setDeliveryDescription] = useState("");
  const [additionalDiscount, setAdditionalDiscount] = useState("");
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Edit Order Modal State
  const [editingOrder, setEditingOrder] = useState(null);
  const [editFormData, setEditFormData] = useState({
    patientName: "", mobile: "", address: "", age: "", gender: "", advance: 0, balance: 0, total: 0
  });
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const handleEditClick = (order) => {
    setEditingOrder(order);
    setEditFormData({
      patientName: order.patientName || "",
      mobile: order.mobile || "",
      address: order.address || "",
      age: order.age || "",
      gender: order.gender || "",
      advance: order.advance || 0,
      balance: order.balance || 0,
      total: order.total || 0
    });
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!editingOrder) return;
    setIsSavingEdit(true);
    try {
      const branchDocRef = doc(db, "local orders", editingOrder.branchDocId);
      await runTransaction(db, async (transaction) => {
        const branchDoc = await transaction.get(branchDocRef);
        if (!branchDoc.exists()) throw new Error("Branch doc not found");
        const data = branchDoc.data();
        const ordersArray = Array.isArray(data.orders) ? data.orders : [];
        const updatedOrders = ordersArray.map(order => {
          if ((order.orderId || order.mobile) === editingOrder.id) {
            const newAdvance = Number(editFormData.advance);
            const total = Number(editFormData.total);
            const newBalance = Math.max(0, total - newAdvance);
            return {
              ...order,
              patientName: editFormData.patientName,
              mobile: editFormData.mobile,
              address: editFormData.address,
              age: editFormData.age,
              gender: editFormData.gender,
              advance: newAdvance,
              balance: newBalance,
              total: total,
              updatedAt: new Date()
            };
          }
          return order;
        });
        transaction.update(branchDocRef, { orders: updatedOrders });
      });
      
      setOrders(prev => prev.map(order => {
        if (order.id === editingOrder.id) {
           const newAdvance = Number(editFormData.advance);
           const total = Number(editFormData.total);
           const newBalance = Math.max(0, total - newAdvance);
           return {
              ...order,
              patientName: editFormData.patientName,
              mobile: editFormData.mobile,
              address: editFormData.address,
              age: editFormData.age,
              gender: editFormData.gender,
              advance: newAdvance,
              balance: newBalance,
              total: total
           };
        }
        return order;
      }));
      setEditingOrder(null);
      alert("Order updated successfully!");
    } catch (error) {
      setError("Failed to update order: " + error.message);
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Let react-chartjs-2 manage chart lifecycle automatically
  // Manual destroy caused resize on detached canvas in Chart.js

  const COMPANY_INFO = {
    name: "Aram Eyecare",
    email: "aramprimaryeyecare@gmail.com",
    phone: "+91 9600481947",
    address:
      "43/9A, Moongilpadi Rd, opposite to sengunthar mini mandabam, Chinnasalem, Tamil Nadu 606201",
    tagline: "An Eye care that cares for you...",
  };

  const fmtINR = (n) => {
    const num = Number(n || 0);
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const generateStatisticsPDF = async () => {
    const doc = new jsPDF();
    
    // Set font to ensure proper character rendering
    doc.setFont("helvetica", "normal");
    
    const now = new Date();
    
    // Load logo
    const imgDataUrl = await (async () => {
      try {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = logoSrc;
        await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        return canvas.toDataURL("image/png");
      } catch (e) {
        return null;
      }
    })();

    // Header
    if (imgDataUrl) {
      doc.addImage(imgDataUrl, "PNG", 14, 10, 20, 20);
    }
    doc.setFontSize(16);
    doc.text(COMPANY_INFO.name, 38, 18);
    doc.setFontSize(10);
    doc.text(COMPANY_INFO.tagline, 38, 24);
    
    doc.setFontSize(14);
    doc.text("Business Analytics Report", 105, 40, { align: "center" });
    
    // Date filter info
    let filterText = `Filter: ${selectedDateFilter === "overall" ? "All Time" : 
      selectedDateFilter === "30days" ? "Last 30 Days" :
      selectedDateFilter === "3months" ? "Last 3 Months" :
      selectedDateFilter === "6months" ? "Last 6 Months" :
      selectedDateFilter === "12months" ? "Last 12 Months" : selectedDateFilter}`;
    
    if (selectedStatusFilter !== "all") {
      filterText += ` | Status: ${selectedStatusFilter}`;
    }
    
    doc.setFontSize(10);
    doc.text(filterText, 105, 50, { align: "center" });
    doc.text(`Generated: ${now.toLocaleDateString()} ${now.toLocaleTimeString()}`, 196, 50, { align: "right" });
    
    // Divider
    doc.setDrawColor(200);
    doc.line(14, 58, 196, 58);
    
    let y = 70;
    
    // Summary Statistics
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Summary Statistics", 14, y);
    y += 10;
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    
    const totalRevenue = filteredOrders.reduce((sum, order) => sum + (order.total || 0), 0);
    const totalAdvances = filteredOrders.reduce((sum, order) => sum + (order.advance || 0), 0);
    const totalBalance = filteredOrders.reduce((sum, order) => sum + (order.balance || 0), 0);
    const averageOrderValue = filteredOrders.length > 0 ? totalRevenue / filteredOrders.length : 0;
    
    const summaryData = [
      ["Total Orders", filteredOrders.length.toString()],
      ["Total Revenue", `Rs. ${fmtINR(totalRevenue)}`],
      ["Total Advances Received", `Rs. ${fmtINR(totalAdvances)}`],
      ["Outstanding Balance", `Rs. ${fmtINR(totalBalance)}`],
      ["Average Order Value", `Rs. ${fmtINR(averageOrderValue)}`],
    ];
    
    autoTable(doc, {
      startY: y,
      head: [["Metric", "Value"]],
      body: summaryData,
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0] },
      columnStyles: { 1: { halign: "right" } },
      margin: { left: 14, right: 14 }
    });
    
    y = doc.lastAutoTable.finalY + 15;
    
    // Orders by Status
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Orders by Status", 14, y);
    y += 10;
    
    const statusData = Object.entries(computeChartData.statusCounts).map(([status, count]) => [
      status,
      count.toString(),
      `Rs. ${fmtINR(computeChartData.statusTotals[status] || 0)}`,
      `${((count / filteredOrders.length) * 100).toFixed(1)}%`
    ]);
    
    autoTable(doc, {
      startY: y,
      head: [["Status", "Count", "Revenue", "Percentage"]],
      body: statusData,
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0] },
      columnStyles: { 
        1: { halign: "right" },
        2: { halign: "right" },
        3: { halign: "right" }
      },
      margin: { left: 14, right: 14 }
    });
    
    y = doc.lastAutoTable.finalY + 15;
    
    // Top Categories
    if (y > 200) {
      doc.addPage();
      y = 30;
    }
    
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Top Product Categories", 14, y);
    y += 10;
    
    const categoryData = Object.entries(computeChartData.categoryCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 10)
      .map(([category, count]) => [
        category,
        count.toString(),
        `${((count / Object.values(computeChartData.categoryCounts).reduce((a, b) => a + b, 0)) * 100).toFixed(1)}%`
      ]);
    
    autoTable(doc, {
      startY: y,
      head: [["Category", "Items Sold", "Percentage"]],
      body: categoryData,
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0] },
      columnStyles: { 
        1: { halign: "right" },
        2: { halign: "right" }
      },
      margin: { left: 14, right: 14 }
    });
    
    y = doc.lastAutoTable.finalY + 15;
    
    // Brand Revenue Analytics
    if (y > 180) {
      doc.addPage();
      y = 30;
    }
    
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Brand-wise Revenue Analysis", 14, y);
    y += 10;
    
    const totalBrandRevenue = Object.values(computeChartData.brandTotals || {}).reduce((a, b) => a + b, 0);
    
    const brandRevenuData = Object.entries(computeChartData.brandTotals || {})
      .sort(([,a], [,b]) => b - a)
      .map(([brand, revenue]) => [
        brand,
        (computeChartData.brandData[brand] || 0).toString(),
        `Rs. ${fmtINR(revenue)}`,
        totalBrandRevenue > 0 ? `${((revenue / totalBrandRevenue) * 100).toFixed(2)}%` : "0%"
      ]);
    
    if (brandRevenuData.length > 0) {
      autoTable(doc, {
        startY: y,
        head: [["Brand Name", "Number of Items", "Total Revenue", "Percentage"]],
        body: brandRevenuData,
        styles: { fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] },
        columnStyles: { 
          1: { halign: "right" },
          2: { halign: "right" },
          3: { halign: "right" }
        },
        margin: { left: 14, right: 14 }
      });
      
      y = doc.lastAutoTable.finalY + 10;
      doc.setFont("helvetica", "bold");
      doc.text(`Total Brand Revenue: Rs. ${fmtINR(totalBrandRevenue)}`, 14, y);
      y = y + 15;
    } else {
      doc.setFont("helvetica", "normal");
      doc.text("No brand data available for this period.", 14, y);
      y += 15;
    }
    
    // Monthly Revenue (if multiple months)
    const monthlyEntries = Object.entries(computeChartData.monthlyData);
    if (monthlyEntries.length > 1) {
      if (y > 180) {
        doc.addPage();
        y = 30;
      }
      
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Monthly Revenue Breakdown", 14, y);
      y += 10;
      
      const monthlyData = monthlyEntries
        .sort(([a], [b]) => new Date(a + " 1, 2000") - new Date(b + " 1, 2000"))
        .map(([month, revenue]) => [month, `Rs. ${fmtINR(revenue)}`]);
      
      autoTable(doc, {
        startY: y,
        head: [["Month", "Revenue"]],
        body: monthlyData,
        styles: { fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0] },
        columnStyles: { 1: { halign: "right" } },
        margin: { left: 14, right: 14 }
      });
    }
    
    doc.save(`Analytics_Report_${now.getTime()}.pdf`);
  };

  const exportOrderPdf = async (order) => {
    const doc = new jsPDF();
    
    // Set font to ensure proper character rendering
    doc.setFont("helvetica", "normal");
    
    const now = new Date();
    const orderDate = order.date instanceof Date ? order.date : new Date(order.date);
    const dateStr = `${orderDate.toLocaleDateString()} ${orderDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    const orderId = order.mobile || order.id;
    const docId = order.id || orderId;

    // Load logo as data URL
    const imgDataUrl = await (async () => {
      try {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = logoSrc;
        await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        return canvas.toDataURL("image/png");
      } catch (e) {
        console.warn("Failed loading logo for PDF:", e);
        return null;
      }
    })();

    // Header
    if (imgDataUrl) {
      doc.addImage(imgDataUrl, "PNG", 14, 10, 20, 20);
    }
    doc.setFontSize(16);
    doc.text(COMPANY_INFO.name, 38, 18);
    doc.setFontSize(10);
    doc.text(COMPANY_INFO.tagline, 38, 24);
    doc.text(`Email: ${COMPANY_INFO.email}`, 38, 30);
    doc.text(`Phone: ${COMPANY_INFO.phone}`, 38, 36);

    doc.setFontSize(12);
    doc.text(`Invoice`, 196, 16, { align: "right" });
    doc.setFontSize(10);
    doc.text(`Bill No: ${order.billNumber || '-'}`, 196, 22, { align: "right" });
    doc.text(`Date: ${dateStr}`, 196, 28, { align: "right" });
    doc.text(`Branch: ${order.branch || '-'}`, 196, 34, { align: "right" });
    doc.text(`Order ID: ${orderId}`, 196, 40, { align: "right" });
    doc.text(`Document ID: ${docId}`, 196, 46, { align: "right" });
    
    // Add delivery date if order is delivered
    if (order.status === "order delivered" && order.deliveredDate) {
      const deliveryDate = order.deliveredDate instanceof Date ? order.deliveredDate : new Date(order.deliveredDate);
      const deliveryDateStr = deliveryDate.toLocaleDateString('en-IN');
      doc.text(`Delivered On: ${deliveryDateStr}`, 196, 52, { align: "right" });
    }

    // divider
    doc.setDrawColor(200);
    doc.line(14, 54, 196, 54);

    // Patient details box
    let y = 60;
    doc.setFontSize(11);
    doc.text("Patient Details", 14, y);
    y += 6;
    doc.setFontSize(10);
    doc.text(`Name: ${order.patientName || '-'}`, 14, y); y += 5;
    doc.text(`Mobile: ${order.mobile || '-'}`, 14, y); y += 5;
    doc.text(`Age/Gender: ${order.age || '-'} / ${order.gender || '-'}`, 14, y); y += 8;
    
    // Add delivery description if available
    if (order.status === "order delivered" && order.deliveryDescription) {
      doc.setFontSize(11);
      doc.text("Delivery Information", 14, y);
      y += 6;
      doc.setFontSize(10);
      
      // Wrap text for delivery description
      const deliveryDescLines = doc.splitTextToSize(order.deliveryDescription, 180);
      doc.text(deliveryDescLines, 14, y);
      y += (deliveryDescLines.length * 5) + 3;
    }

    // Items table
    const rows = (order.orderItems || []).map((it) => [
      it.type || '-',
      it.productName || '-',
      it.category || '-',
      it.productId || '-',
      String(it.quantity || 0),
      fmtINR(it.cost || 0),
      fmtINR(it.total || 0),
    ]);

    autoTable(doc, {
      startY: y,
      head: [["Type", "Name", "Category", "Product ID", "Qty", "Cost", "Total"]],
      body: rows,
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [0, 0, 0], textColor: 255 },
      columnStyles: { 4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" } },
    });

    // Totals Summary (match Sales)
    const afterTableY = (doc.lastAutoTable && doc.lastAutoTable.finalY ? doc.lastAutoTable.finalY : y) + 10;
    doc.setFontSize(12);
    doc.text("BILLING SUMMARY", 105, afterTableY, { align: "center" });
    doc.setDrawColor(0);
    doc.rect(60, afterTableY + 5, 90, 64);

    const lines = [
      ["Subtotal", fmtINR(order.subtotal || 0)],
      ["Discount", `- ${fmtINR(order?.discount?.amount || 0)}${order?.discount?.type === 'percent' ? ` (${order?.discount?.value}%)` : ''}`],
      ["Coopound", `- ${fmtINR(order?.coopound?.amount || 0)}${order?.coopound?.code ? ` (${order?.coopound?.code})` : ''}`],
      ["Taxable", fmtINR((order.subtotal || 0) - (order?.discount?.amount || 0) - (order?.coopound?.amount || 0))],
      [`GST (${(order.gstRate || 0)}%)`, fmtINR(order.gstAmount || 0)],
      ["Total", fmtINR(order.total || 0)],
      ["Advance", fmtINR(order.advance || 0)],
      ["Balance", fmtINR(order.balance || 0)],
    ];

    let sumY = afterTableY + 15;
    doc.setFontSize(10);
    lines.forEach(([label, value], index) => {
      if (index === lines.length - 1) {
        doc.setFontSize(11);
        doc.setFont(undefined, "bold");
      }
      doc.text(label, 65, sumY);
      doc.text(value, 145, sumY, { align: "right" });
      sumY += 7;
    });
    doc.setFont(undefined, "normal");

    // Footer
    doc.setFontSize(9);
    doc.text("Thank you for your purchase!", 14, sumY + 6);
    doc.text("This is a computer generated invoice.", 14, sumY + 12);
    doc.text(`${COMPANY_INFO.name} • ${COMPANY_INFO.phone} • ${COMPANY_INFO.email}`, 14, sumY + 18);

    // Add second page for terms and conditions
    doc.addPage();
    
    // Header for second page
    if (imgDataUrl) {
      doc.addImage(imgDataUrl, "PNG", 14, 10, 20, 20);
    }
    doc.setFontSize(16);
    doc.text(COMPANY_INFO.name, 38, 18);
    doc.setFontSize(12);
    doc.text("Terms & Conditions", 38, 28);
    
    // Divider
    doc.setDrawColor(200);
    doc.line(14, 35, 196, 35);
    
    // Terms and conditions content
    let termsY = 45;
    doc.setFontSize(10);
    
    const termsContent = [
      { title: "1. Advance Payment Policy", content: [
        "While placing an order for spectacles or contact lenses, the patient/customer is required to pay an",
        "advance amount ranging from 25% to 100% of the total bill value.",
        "Orders will be processed only after receipt of the advance payment."
      ]},
      { title: "2. Delivery Timeline", content: [
        "Delivery time may vary depending on the type of lenses, frames, or customizations selected.",
        "Standard delivery time is usually 3–4 working days, but may extend for special or customized products."
      ]},
      { title: "3. Outside Prescription (Out-Prescription) Policy", content: [
        "Prescriptions provided from other hospitals or optical stores will be processed as given.",
        "In case of any adaptation issues, visual discomfort, or complaints related to the prescription power,",
        "we shall not be responsible, and such cases will not be eligible for warranty, exchange, or claims."
      ]},
      { title: "4. Product Availability", content: [
        "Certain frames or lens products may require additional time for procurement.",
        "Customers requesting specific models or brands are kindly requested to wait for the communicated",
        "delivery period."
      ]},
      { title: "5. Warranty Policy", content: [
        "Adaptation warranty for progressive and myocare lenses are upto 3 weeks.",
        "Warranty is applicable only for selected products as specified at the time of purchase.",
        "Warranty claims can be made only once per product.",
        "Warranty is subject to the respective company's terms and conditions.",
        "Scratches on lenses or frames are not covered under warranty."
      ]},
      { title: "6. Cancellation Policy", content: [
        "Cancellation should be done within a day(24 hrs) of ordering, more than a day is not applicable",
        "for Cancellation.",
        "Once an order is confirmed, cancellation is not eligible for refund."
      ]},
      { title: "7. Exchange Policy", content: [
        "Exchange is applicable only for manufacturing defects, fitting complaints, or frame-related complaints.",
        "Exchange requests must be made within 7 days from the date of delivery.",
        "In case the customer is unavailable within this period, prior notification must be provided.",
        "Exchange or return is not applicable for progressive lens adaptation complaints."
      ]},
      { title: "8. Return & Refund Policy", content: [
        "Return or refund is not applicable for any products once delivered."
      ]}
    ];
    
    termsContent.forEach((section, index) => {
      // Check if we need to add a new page
      if (termsY > 270) {
        doc.addPage();
        termsY = 20;
      }
      
      // Section title
      doc.setFont(undefined, "bold");
      doc.text(section.title, 14, termsY);
      doc.setFont(undefined, "normal");
      termsY += 6;
      
      // Section content
      section.content.forEach(line => {
        if (termsY > 270) {
          doc.addPage();
          termsY = 20;
        }
        doc.text(line, 14, termsY);
        termsY += 4;
      });
      
      termsY += 3; // Extra space between sections
    });

    // Add third page for patient examination details
    doc.addPage();
    
    // Header for third page
    if (imgDataUrl) {
      doc.addImage(imgDataUrl, "PNG", 14, 10, 20, 20);
    }
    doc.setFontSize(16);
    doc.text(COMPANY_INFO.name, 38, 18);
    doc.setFontSize(12);
    doc.text("Patient Examination Details", 38, 28);
    
    doc.setFontSize(10);
    doc.text(`Date: ${dateStr}`, 196, 16, { align: "right" });
    doc.text(`Order ID: ${orderId}`, 196, 22, { align: "right" });
    
    // Divider
    doc.setDrawColor(200);
    doc.line(14, 40, 196, 40);
    
    // Patient details section
    let pageY = 50;
    doc.setFontSize(12);
    doc.text("Patient Information", 14, pageY);
    pageY += 8;
    
    doc.setFontSize(10);
    doc.text(`Name: ${order.patientName || '-'}`, 14, pageY);
    pageY += 6;
    doc.text(`Age: ${order.age || '-'}`, 14, pageY);
    doc.text(`Gender: ${order.gender || '-'}`, 100, pageY);
    pageY += 6;
    doc.text(`Mobile: ${order.mobile || '-'}`, 14, pageY);
    doc.text(`Address: ${order.address || '-'}`, 100, pageY);
    pageY += 15;
    
    // Acceptance table
    doc.setFontSize(12);
    doc.text("Acceptance", 14, pageY);
    pageY += 8;
    
    const acceptanceData = order.acceptance && typeof order.acceptance === 'object' ? order.acceptance : {};
    const acceptanceRows = [
      [
        "OD",
        (acceptanceData.od && acceptanceData.od.ucva) ? String(acceptanceData.od.ucva) : "-",
        (acceptanceData.od && acceptanceData.od.sph) ? String(acceptanceData.od.sph) : "-",
        (acceptanceData.od && acceptanceData.od.cyl) ? String(acceptanceData.od.cyl) : "-",
        (acceptanceData.od && acceptanceData.od.a) ? String(acceptanceData.od.a) : "-",
        (acceptanceData.od && acceptanceData.od.bcva) ? String(acceptanceData.od.bcva) : "-",
        (acceptanceData.od && acceptanceData.od.duoChrome) ? String(acceptanceData.od.duoChrome) : "-",
        (acceptanceData.od && acceptanceData.od.jcc) ? String(acceptanceData.od.jcc) : "-"
      ],
      [
        "OS",
        (acceptanceData.os && acceptanceData.os.ucva) ? String(acceptanceData.os.ucva) : "-",
        (acceptanceData.os && acceptanceData.os.sph) ? String(acceptanceData.os.sph) : "-",
        (acceptanceData.os && acceptanceData.os.cyl) ? String(acceptanceData.os.cyl) : "-",
        (acceptanceData.os && acceptanceData.os.a) ? String(acceptanceData.os.a) : "-",
        (acceptanceData.os && acceptanceData.os.bcva) ? String(acceptanceData.os.bcva) : "-",
        (acceptanceData.os && acceptanceData.os.duoChrome) ? String(acceptanceData.os.duoChrome) : "-",
        (acceptanceData.os && acceptanceData.os.jcc) ? String(acceptanceData.os.jcc) : "-"
      ],
      [
        "Add",
        (acceptanceData.add) ? String(acceptanceData.add) : "-",
        "",
        "",
        "",
        "",
        "",
        ""
      ]
    ];
    
    autoTable(doc, {
      startY: pageY,
      head: [["", "UCVA", "Sph", "Cyl", "A", "BCVA", "Duo-chrome", "Jcc"]],
      body: acceptanceRows,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0] },
      columnStyles: { 
        0: { fontStyle: "bold", halign: "center" },
        1: { halign: "center" },
        2: { halign: "center" },
        3: { halign: "center" },
        4: { halign: "center" },
        5: { halign: "center" },
        6: { halign: "center" },
        7: { halign: "center" }
      },
      tableWidth: 'auto',
      margin: { left: 14, right: 14 }
    });

    doc.save(`Invoice_${orderId}_${now.getTime()}.pdf`);
  };

  const getStatusPriority = (status) => {
    switch (status) {
      case "order ready": return 1;
      case "order booked": return 2;
      case "order delivered": return 3;
      case "order cancelled": return 4;
      default: return 5;
    }
  };

  useEffect(() => {
    const fetchOrders = async () => {
      if (userBranch === null) return; // Wait until branch is loaded
      
      setIsLoading(true);
      setError("");
      try {
        const fetchedOrders = [];
        
        if (userBranch === 'ADMIN') {
          // Admin can see all branches
          const querySnapshot = await getDocs(collection(db, "local orders"));
          querySnapshot.docs.forEach((docSnap) => {
            const data = docSnap.data();
            const orders = Array.isArray(data.orders) ? data.orders : [];
            
            orders.forEach((order) => {
              fetchedOrders.push({
                ...order,
                branchDocId: docSnap.id, // Store the branch document ID for updates
                id: order.orderId || order.mobile || docSnap.id, // Use orderId as primary ID
                date: order.date ? order.date.toDate() : new Date(),
              });
            });
          });
        } else {
          // Normal user sees only their branch
          const docSnap = await getDoc(doc(db, "local orders", userBranch));
          if (docSnap.exists()) {
            const data = docSnap.data();
            const orders = Array.isArray(data.orders) ? data.orders : [];
            
            orders.forEach((order) => {
              fetchedOrders.push({
                ...order,
                branchDocId: docSnap.id,
                id: order.orderId || order.mobile || docSnap.id,
                date: order.date ? order.date.toDate() : new Date(),
              });
            });
          }
        }
        
        fetchedOrders.sort((a, b) => {
          const priorityA = getStatusPriority(a.status);
          const priorityB = getStatusPriority(b.status);
          if (priorityA !== priorityB) return priorityA - priorityB;
          return new Date(b.date) - new Date(a.date);
        });
        setOrders(fetchedOrders);
      } catch (err) {
        setError("Error fetching orders: " + err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchOrders();
  }, [userBranch]);

  // No manual destroy; react-chartjs-2 cleans up on unmount

  const getCollectionName = (type) => {
    switch (type) {
      case "frames": return "Local Inventory";
      case "lens": return "Lens Local";
      case "contactlens": return "ContactLens Local";
      case "sunglassess": return "sunglassess local";
      case "accessories": return "Accessories Local";
      case "storeinventory": return "Store Inventory Local";
      case "serviceaccessories": return "Service Accessories Local";
      default: return "Local Inventory";
    }
  };

  const updateStatus = async (orderId, newStatus) => {
    // If changing to delivered and there's a balance, show payment modal
    if (newStatus === "order delivered") {
      const orderToCheck = orders.find(order => order.id === orderId);
      if (orderToCheck && (orderToCheck.balance || 0) > 0) {
        setPaymentOrder(orderToCheck);
        setPaymentAmount("");
        setShowPaymentModal(true);
        return; // Don't update status yet, wait for payment
      }
    }
    
    setUpdatingOrder(orderId);
    try {
      // Find the order to get its branch document ID
      const orderToUpdate = orders.find(order => order.id === orderId);
      if (!orderToUpdate) {
        throw new Error("Order not found");
      }
      
      const branchDocRef = doc(db, "local orders", orderToUpdate.branchDocId);
      
      // Use runTransaction to safely update the order within the array
      await runTransaction(db, async (transaction) => {
        const branchDoc = await transaction.get(branchDocRef);
        
        if (!branchDoc.exists()) {
          throw new Error("Branch document not found");
        }
        
        // Prepare reads for inventory items if cancelling
        let itemDocsMap = new Map();
        const orderItems = orderToUpdate.orderItems || [];
        if (newStatus === "order cancelled") {
            for (const item of orderItems) {
                if (item.type !== "consultancy" && item.productId) {
                    const collName = getCollectionName(item.type);
                    const itemRef = doc(db, collName, item.productId);
                    const itemDoc = await transaction.get(itemRef);
                    if (itemDoc.exists()) {
                        itemDocsMap.set(item.productId, { ref: itemRef, doc: itemDoc, quantityToAdd: Number(item.quantity || 1) });
                    }
                }
            }
        }
        
        const data = branchDoc.data();
        const ordersArray = Array.isArray(data.orders) ? data.orders : [];
        
        // Find and update the specific order
        const updatedOrders = ordersArray.map(order => {
          if ((order.orderId || order.mobile) === orderId) {
            const updatedOrder = {
              ...order,
              status: newStatus,
              updatedAt: new Date()
            };
            // Add delivered date when status changes to "order delivered"
            if (newStatus === "order delivered") {
              updatedOrder.deliveredDate = new Date();
            }
            if (newStatus === "order cancelled") {
              updatedOrder.balance = 0;
            }
            return updatedOrder;
          }
          return order;
        });
        
        transaction.update(branchDocRef, { orders: updatedOrders });
        
        if (newStatus === "order cancelled") {
            itemDocsMap.forEach((info) => {
                const currentQty = Number(info.doc.data().Quantity || 0);
                transaction.update(info.ref, { Quantity: currentQty + info.quantityToAdd });
            });
        }
      });
      
      setOrders((prevOrders) =>
        prevOrders.map((order) => {
          if (order.id === orderId) {
            const updatedOrder = { ...order, status: newStatus };
            if (newStatus === "order delivered") {
              updatedOrder.deliveredDate = new Date();
            }
            if (newStatus === "order cancelled") {
              updatedOrder.balance = 0;
            }
            return updatedOrder;
          }
          return order;
        })
      );
      setOrders((prev) =>
        [...prev].sort((a, b) => {
          const priorityA = getStatusPriority(a.status);
          const priorityB = getStatusPriority(b.status);
          if (priorityA !== priorityB) return priorityA - priorityB;
          return new Date(b.date) - new Date(a.date);
        })
      );
      alert(`Status updated to "${newStatus}" successfully!`);
    } catch (err) {
      setError("Error updating status: " + err.message);
    } finally {
      setUpdatingOrder(null);
    }
  };

  const processPayment = async () => {
    if (!paymentOrder) return;
    
    // Allow empty inputs to count as 0
    const payment = Number(paymentAmount) || 0;
    const extraDiscount = Number(additionalDiscount) || 0;
    
    const currentBalance = paymentOrder.balance || 0;

    if (payment < 0 || extraDiscount < 0) {
      alert("Amounts cannot be negative");
      return;
    }

    if (payment + extraDiscount > currentBalance) {
      alert(`Total deduction (Payment + Discount: ₹${(payment + extraDiscount).toFixed(2)}) cannot exceed the original balance of ₹${currentBalance.toFixed(2)}`);
      return;
    }

    setIsProcessingPayment(true);
    try {
      const branchDocRef = doc(db, "local orders", paymentOrder.branchDocId);
      
      await runTransaction(db, async (transaction) => {
        const branchDoc = await transaction.get(branchDocRef);
        
        if (!branchDoc.exists()) {
          throw new Error("Branch document not found");
        }
        
        const data = branchDoc.data();
        const orders = Array.isArray(data.orders) ? data.orders : [];
        
        // Find and update the specific order with payment
        const updatedOrders = orders.map(order => {
          if ((order.orderId || order.mobile) === paymentOrder.id) {
            const newTotal = Math.max(0, (order.total || 0) - extraDiscount);
            const newAdvance = (order.advance || 0) + payment;
            const newBalance = Math.max(0, newTotal - newAdvance);
            const currentDiscounts = order.discount || { amount: 0, type: 'amount' };
            
            return {
              ...order,
              total: newTotal,
              discount: {
                ...currentDiscounts,
                amount: Number(currentDiscounts.amount || 0) + extraDiscount
              },
              additionalDiscountLog: [
                ...(order.additionalDiscountLog || []),
                {
                  amount: extraDiscount,
                  date: new Date()
                }
              ],
              advance: newAdvance,
              balance: newBalance,
              status: "order delivered",
              deliveredDate: new Date(),
              deliveryDescription: deliveryDescription,
              paymentHistory: payment > 0 ? [
                ...(order.paymentHistory || []),
                {
                  amount: payment,
                  date: new Date(),
                  type: "delivery_payment"
                }
              ] : (order.paymentHistory || []),
              updatedAt: new Date()
            };
          }
          return order;
        });
        
        transaction.update(branchDocRef, { orders: updatedOrders });
      });

      // Update local state
      setOrders((prevOrders) =>
        prevOrders.map((order) => {
          if (order.id === paymentOrder.id) {
            const newTotal = Math.max(0, (order.total || 0) - extraDiscount);
            const newAdvance = (order.advance || 0) + payment;
            const newBalance = Math.max(0, newTotal - newAdvance);
            const currentDiscounts = order.discount || { amount: 0, type: 'amount' };
            return {
              ...order,
              total: newTotal,
              discount: {
                ...currentDiscounts,
                amount: Number(currentDiscounts.amount || 0) + extraDiscount
              },
              advance: newAdvance,
              balance: newBalance,
              status: "order delivered",
              deliveredDate: new Date(),
              deliveryDescription: deliveryDescription
            };
          }
          return order;
        })
      );

      // Re-sort orders
      setOrders((prev) =>
        [...prev].sort((a, b) => {
          const priorityA = getStatusPriority(a.status);
          const priorityB = getStatusPriority(b.status);
          if (priorityA !== priorityB) return priorityA - priorityB;
          return new Date(b.date) - new Date(a.date);
        })
      );

      setShowPaymentModal(false);
      setPaymentOrder(null);
      setPaymentAmount("");
      setAdditionalDiscount("");
      setDeliveryDescription("");
      alert(`Delivery processed! Payment collected: ₹${payment.toFixed(2)}${extraDiscount > 0 ? `, Discount given: ₹${extraDiscount.toFixed(2)}` : ""}`);
    } catch (err) {
      setError("Error processing payment: " + err.message);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const filteredOrders = useMemo(() => {
    let filtered = orders;
    
    // Apply bill number search
    if (searchBillNumber.trim()) {
      const billNum = searchBillNumber.trim();
      filtered = filtered.filter((order) => 
        order.billNumber && String(order.billNumber).includes(billNum)
      );
    }
    
    // Apply patient name or mobile search
    if (searchPatient.trim()) {
      const query = searchPatient.trim().toLowerCase();
      filtered = filtered.filter((order) => 
        (order.mobile && String(order.mobile).includes(query)) ||
        (order.patientName && String(order.patientName).toLowerCase().includes(query))
      );
    }
    
    // Apply date filter
    if (selectedDateFilter !== "overall") {
      const now = new Date();
      let cutoffDate;
      
      switch (selectedDateFilter) {
        case "custom":
          if (customStartDate && customEndDate) {
            const start = new Date(customStartDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(customEndDate);
            end.setHours(23, 59, 59, 999);
            filtered = filtered.filter((order) => order.date >= start && order.date <= end);
          }
          break;
        case "30days":
          cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case "3months":
          cutoffDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
          break;
        case "6months":
          cutoffDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
          break;
        case "12months":
          cutoffDate = new Date(now.getFullYear(), now.getMonth() - 12, now.getDate());
          break;
        default:
          cutoffDate = null;
      }
      
      if (cutoffDate) {
        filtered = filtered.filter((order) => order.date >= cutoffDate);
      }
    }
    
    // Apply status filter
    if (selectedStatusFilter !== "all") {
      filtered = filtered.filter((order) => (order.status || "order booked") === selectedStatusFilter);
    }
    
    return filtered;
  }, [orders, selectedStatusFilter, selectedDateFilter, searchBillNumber, searchPatient, customStartDate, customEndDate]);

  const groupedOrders = useMemo(() => {
    const groups = {};
    filteredOrders.forEach((order) => {
      const status = order.status || "order booked";
      if (!groups[status]) groups[status] = [];
      groups[status].push(order);
    });
    return groups;
  }, [filteredOrders]);

  const allStatuses = useMemo(() =>
    Object.keys(groupedOrders).sort((a, b) => getStatusPriority(a) - getStatusPriority(b)),
    [groupedOrders]
  );

  const statusCounts = useMemo(() => {
    const counts = { all: filteredOrders.length };
    filteredOrders.forEach((order) => {
      const status = order.status || "order booked";
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [filteredOrders]);

  const computeChartData = useMemo(() => {
    const statusCounts = {};
    const statusTotals = {};
    const categoryCounts = {};
    const monthlyData = {};
    const brandData = {};
    const brandTotals = {};

    filteredOrders.forEach((order) => {
      const statusKey = order.status || "order booked";
      statusCounts[statusKey] = (statusCounts[statusKey] || 0) + 1;
      statusTotals[statusKey] = (statusTotals[statusKey] || 0) + (order.total || 0);

      order.orderItems?.forEach((item) => {
        const catKey = item.category || "Unknown";
        categoryCounts[catKey] = (categoryCounts[catKey] || 0) + 1;

        // Brand Revenue Analytics
        const brand = item.brand || "Unbranded";
        brandData[brand] = (brandData[brand] || 0) + 1;
        brandTotals[brand] = (brandTotals[brand] || 0) + (item.total || 0);
      });

      const monthKey = order.date.toLocaleDateString("en-US", { year: "numeric", month: "short" });
      monthlyData[monthKey] = (monthlyData[monthKey] || 0) + (order.total || 0);
    });

    return { statusCounts, statusTotals, categoryCounts, monthlyData, brandData, brandTotals };
  }, [filteredOrders]);

  const getDynamicColors = (count, type = 'pie') => {
    const pieColors = ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF", "#FF9F40", "#FF6B6B", "#4ECDC4", "#45B7D1", "#FFA07A", "#98D8C8", "#F7DC6F"];
    const barColors = ["#36A2EB"];
    const brandColors = ["#E74C3C", "#3498DB", "#2ECC71", "#F39C12", "#9B59B6", "#1ABC9C", "#E67E22", "#34495E"];
    
    if (type === 'brand') return brandColors.slice(0, count);
    return type === 'bar' ? barColors : pieColors.slice(0, count);
  };

  const pieData = {
    labels: Object.keys(computeChartData.statusCounts),
    datasets: [{ label: "Orders", data: Object.values(computeChartData.statusCounts), backgroundColor: getDynamicColors(Object.keys(computeChartData.statusCounts).length) }],
  };

  const barData = {
    labels: Object.keys(computeChartData.statusTotals),
    datasets: [{ label: "Total Amount (Rs.)", data: Object.values(computeChartData.statusTotals), backgroundColor: getDynamicColors(1, 'bar') }],
  };

  const topCategories = Object.entries(computeChartData.categoryCounts)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 5)
    .reduce((acc, [key, value]) => {
      acc.labels.push(key);
      acc.data.push(value);
      return acc;
    }, { labels: [], data: [] });

  const topBrands = Object.entries(computeChartData.brandTotals)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 8)
    .reduce((acc, [key, value]) => {
      acc.labels.push(key);
      acc.data.push(value);
      return acc;
    }, { labels: [], data: [] });

  const categoryPieData = {
    labels: topCategories.labels,
    datasets: [{ label: "Number of Items by Category", data: topCategories.data, backgroundColor: getDynamicColors(topCategories.labels.length) }],
  };

  const brandRevenueData = {
    labels: topBrands.labels,
    datasets: [{ label: "Revenue by Brand (Rs.)", data: topBrands.data, backgroundColor: getDynamicColors(topBrands.labels.length, 'brand') }],
  };

  const allBrandsTotalData = {
    labels: Object.keys(computeChartData.brandTotals),
    datasets: [{ label: "Total Revenue by Brand", data: Object.values(computeChartData.brandTotals), backgroundColor: getDynamicColors(Object.keys(computeChartData.brandTotals).length) }],
  };

  const lineData = {
    labels: Object.keys(computeChartData.monthlyData).sort(),
    datasets: [{
      label: "Monthly Total (Rs.)",
      data: Object.keys(computeChartData.monthlyData).sort().map(k => computeChartData.monthlyData[k]),
      borderColor: "#36A2EB",
      backgroundColor: "rgba(54, 162, 235, 0.2)",
      tension: 0.1,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: { display: false },
    },
  };

  if (isLoading) return <div className="text-center my-20 text-lg">Loading orders...</div>;
  if (error) return <div className="text-red-600 text-center my-10 p-4 bg-red-50 rounded">{error}</div>;

  return (
    <div className="max-w-7xl mx-auto my-8 px-4 sm:px-6 lg:px-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center md:text-left">Order Management Dashboard</h1>

      {/* ─── CHARTS SECTION ──────────────────────────────────────── */}
      <div className="mb-12 p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6">Analytics Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="h-72 bg-gray-50 p-4 rounded-lg border border-gray-100">
            <h3 className="text-lg font-medium mb-3 text-center">Orders by Status</h3>
            <div className="h-56"><Pie data={pieData} options={chartOptions} /></div>
          </div>

          <div className="h-72 bg-gray-50 p-4 rounded-lg border border-gray-100">
            <h3 className="text-lg font-medium mb-3 text-center">Revenue by Status</h3>
            <div className="h-56"><Bar data={barData} options={chartOptions} /></div>
          </div>

          <div className="h-72 bg-gray-50 p-4 rounded-lg border border-gray-100">
            <h3 className="text-lg font-medium mb-3 text-center">Top Categories</h3>
            <div className="h-56"><Pie data={categoryPieData} options={chartOptions} /></div>
          </div>

          <div className="h-72 bg-gray-50 p-4 rounded-lg border border-gray-100">
            <h3 className="text-lg font-medium mb-3 text-center">🏷️ Top Brands Revenue</h3>
            <div className="h-56"><Pie data={brandRevenueData} options={chartOptions} /></div>
          </div>

          <div className="h-72 bg-gray-50 p-4 rounded-lg border border-gray-100">
            <h3 className="text-lg font-medium mb-3 text-center">💰 All Brands Revenue</h3>
            <div className="h-56"><Bar data={allBrandsTotalData} options={chartOptions} /></div>
          </div>

          <div className="md:col-span-2 lg:col-span-3 h-80 bg-gray-50 p-4 rounded-lg border border-gray-100">
            <h3 className="text-lg font-medium mb-3 text-center">📈 Monthly Revenue Trend</h3>
            <div className="h-64"><Line data={lineData} options={chartOptions} /></div>
          </div>
        </div>
      </div>

      {/* ─── DATE FILTER & PDF EXPORT ─────────────────────────────────── */}
      <div className="mb-6 bg-white shadow rounded-lg overflow-hidden border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-700">Date Range & Analytics</h2>
            <button
              onClick={generateStatisticsPDF}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
            >
              📊 Download Analytics Report
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 px-6 py-5">
          <button
            onClick={() => setSelectedDateFilter("overall")}
            className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
              selectedDateFilter === "overall"
                ? "bg-purple-600 text-white shadow-md"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Overall
          </button>
          
          <button
            onClick={() => setSelectedDateFilter("30days")}
            className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
              selectedDateFilter === "30days"
                ? "bg-purple-600 text-white shadow-md"
                : "bg-purple-50 text-purple-700 hover:bg-purple-100"
            }`}
          >
            Last 30 Days
          </button>

          <button
            onClick={() => setSelectedDateFilter("3months")}
            className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
              selectedDateFilter === "3months"
                ? "bg-purple-600 text-white shadow-md"
                : "bg-purple-50 text-purple-700 hover:bg-purple-100"
            }`}
          >
            Last 3 Months
          </button>

          <button
            onClick={() => setSelectedDateFilter("6months")}
            className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
              selectedDateFilter === "6months"
                ? "bg-purple-600 text-white shadow-md"
                : "bg-purple-50 text-purple-700 hover:bg-purple-100"
            }`}
          >
            Last 6 Months
          </button>

          <button
            onClick={() => setSelectedDateFilter("12months")}
            className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
              selectedDateFilter === "12months"
                ? "bg-purple-600 text-white shadow-md"
                : "bg-purple-50 text-purple-700 hover:bg-purple-100"
            }`}
          >
            Last 12 Months
          </button>
          
          <button
            onClick={() => setSelectedDateFilter("custom")}
            className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
              selectedDateFilter === "custom"
                ? "bg-purple-600 text-white shadow-md"
                : "bg-purple-50 text-purple-700 hover:bg-purple-100"
            }`}
          >
            Custom Range
          </button>
        </div>
        
        {selectedDateFilter === "custom" && (
          <div className="px-6 py-4 bg-purple-50 border-t border-purple-100 flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-purple-800">Start Date:</label>
              <input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} className="px-3 py-1.5 border border-purple-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white" />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-purple-800">End Date:</label>
              <input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} className="px-3 py-1.5 border border-purple-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white" />
            </div>
          </div>
        )}
      </div>

      {/* ─── SEARCH BAR ─────────────────────────────────────────────── */}
      <div className="mb-10 bg-white shadow-sm rounded-xl overflow-hidden border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <h2 className="text-lg font-bold text-gray-800">Search Orders</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-6 py-6 bg-white">
          <div className="relative">
            <label className="block text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wider">Bill Number</label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                #
              </span>
              <input
                type="text"
                placeholder="e.g. 21, 22, 23..."
                value={searchBillNumber}
                onChange={(e) => setSearchBillNumber(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-black focus:border-black transition-all"
              />
            </div>
          </div>
          <div className="relative">
            <label className="block text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wider">Patient Details</label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 font-serif italic text-lg">
                ⚲
              </span>
              <input
                type="text"
                placeholder="Search by Name or Phone Number..."
                value={searchPatient}
                onChange={(e) => setSearchPatient(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-black focus:border-black transition-all"
              />
            </div>
          </div>
        </div>
        {(searchBillNumber || searchPatient) && (
          <div className="px-6 py-3 bg-gray-900 border-t border-gray-800 text-sm text-white font-medium flex items-center justify-between">
            <span>Found: {filteredOrders.length} matching order(s)</span>
            <button 
              onClick={() => { setSearchBillNumber(""); setSearchPatient(""); }}
              className="text-gray-300 hover:text-white text-xs uppercase tracking-wider font-bold transition-colors"
            >
              Clear Search
            </button>
          </div>
        )}
      </div>

      {/* ─── STATUS FILTER BAR ─────────────────────────────────────────── */}
      <div className="mb-10 bg-white shadow rounded-lg overflow-hidden border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-700">Filter by Status</h2>
        </div>
        <div className="flex flex-wrap gap-3 px-6 py-5">
          <button
            onClick={() => setSelectedStatusFilter("all")}
            className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
              selectedStatusFilter === "all"
                ? "bg-indigo-600 text-white shadow-md"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            All ({statusCounts.all || 0})
          </button>

          <button
            onClick={() => setSelectedStatusFilter("order booked")}
            className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
              selectedStatusFilter === "order booked"
                ? "bg-blue-600 text-white shadow-md"
                : "bg-blue-50 text-blue-700 hover:bg-blue-100"
            }`}
          >
            Booked ({statusCounts["order booked"] || 0})
          </button>

          <button
            onClick={() => setSelectedStatusFilter("order ready")}
            className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
              selectedStatusFilter === "order ready"
                ? "bg-amber-500 text-white shadow-md"
                : "bg-amber-50 text-amber-700 hover:bg-amber-100"
            }`}
          >
            Ready ({statusCounts["order ready"] || 0})
          </button>

          <button
            onClick={() => setSelectedStatusFilter("order delivered")}
            className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
              selectedStatusFilter === "order delivered"
                ? "bg-emerald-600 text-white shadow-md"
                : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
            }`}
          >
            Delivered ({statusCounts["order delivered"] || 0})
          </button>

          <button
            onClick={() => setSelectedStatusFilter("order cancelled")}
            className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
              selectedStatusFilter === "order cancelled"
                ? "bg-red-600 text-white shadow-md"
                : "bg-red-50 text-red-700 hover:bg-red-100"
            }`}
          >
            Cancelled ({statusCounts["order cancelled"] || 0})
          </button>
        </div>
      </div>

      {/* ─── ORDERS LIST ─────────────────────────────────────────────── */}
      {allStatuses.length === 0 && filteredOrders.length === 0 ? (
        <div className="text-center py-16 text-gray-500 bg-gray-50 rounded-xl border border-dashed border-gray-300">
          No orders found {selectedStatusFilter !== "all" ? `matching filter: "${selectedStatusFilter.replace(/_/g, " ")}"` : ""}
        </div>
      ) : (
        allStatuses.map((status) => (
          <section key={status} className="mb-14">
            <h2 className="text-2xl font-bold text-gray-800 mb-5 capitalize flex items-baseline gap-3">
              {status.replace(/_/g, " ")}
              <span className="text-xl font-normal text-gray-500">({groupedOrders[status]?.length || 0})</span>
            </h2>

            <div className="space-y-6">
              {groupedOrders[status].map((order) => (
                <div
                  key={order.id}
                  className="border border-gray-200 rounded-xl p-6 bg-white shadow-sm hover:shadow-md transition-shadow duration-200"
                >
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-5 gap-4">
                    <div>
                      <h3 className="font-semibold text-lg text-gray-900">{order.patientName}</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {order.gender}, {order.age} yrs • {order.mobile}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {order.date.toLocaleDateString()} {order.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <div className="text-right sm:text-right">
                      <p className="text-xl font-bold text-gray-900">₹{order.total?.toFixed(2) || "0.00"}</p>
                    </div>
                  </div>

                  <div className="mb-5 overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium">Item</th>
                          <th className="px-4 py-3 text-left font-medium">Category</th>
                          <th className="px-4 py-3 text-center font-medium">Qty</th>
                          <th className="px-4 py-3 text-right font-medium">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {order.orderItems?.map((item, i) => (
                          <tr key={i} className="hover:bg-gray-50/70 transition-colors">
                            <td className="px-4 py-3">{item.productName}</td>
                            <td className="px-4 py-3 text-gray-600">{item.category || "—"}</td>
                            <td className="px-4 py-3 text-center">{item.quantity}</td>
                            <td className="px-4 py-3 text-right font-medium">₹{item.total?.toFixed(2) || "0.00"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-700">Current Status:</span>
                        <span className={`px-4 py-1.5 rounded-full text-xs font-semibold ${
                          status === "order cancelled" ? "bg-red-100 text-red-800" :
                          status === "order ready" ? "bg-amber-100 text-amber-800" :
                          status === "order booked" ? "bg-blue-100 text-blue-800" :
                          "bg-emerald-100 text-emerald-800"
                        }`}>
                          {status.replace(/_/g, " ").toUpperCase()}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-700">Payment Status:</span>
                        {(order.balance || 0) === 0 ? (
                          <span className="px-4 py-1.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                            PAID
                          </span>
                        ) : (
                          <span className="px-4 py-1.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                            NOT PAID (₹{(order.balance || 0).toFixed(2)} due)
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <select
                        value={order.status || "order booked"}
                        onChange={(e) => updateStatus(order.id, e.target.value)}
                        disabled={updatingOrder === order.id}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 disabled:opacity-50 bg-white min-w-[180px]"
                      >
                        <option value="order booked">Order Booked</option>
                        <option value="order ready">Order Ready</option>
                        <option value="order delivered">Order Delivered</option>
                        <option value="order cancelled">Order Cancelled</option>
                      </select>

                      {updatingOrder === order.id && (
                        <span className="text-indigo-600 text-sm font-medium animate-pulse">Updating...</span>
                      )}

                      <button
                        type="button"
                        onClick={() => handleEditClick(order)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        title="Edit order details"
                      >
                        Edit Details
                      </button>
                      <button
                        type="button"
                        onClick={() => exportOrderPdf(order)}
                        className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
                        title="Download order invoice as PDF"
                      >
                        Download PDF
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))
      )}
      
      {/* Payment Modal */}
      {showPaymentModal && paymentOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gray-50 rounded-t-xl">
              <h3 className="text-xl font-bold text-gray-800">Complete Delivery</h3>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setPaymentOrder(null);
                  setPaymentAmount("");
                  setAdditionalDiscount("");
                  setDeliveryDescription("");
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors text-2xl leading-none"
              >
                &times;
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              {/* Summary Banner */}
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-sm text-blue-900">Patient:</p>
                  <p className="font-semibold text-blue-900">{paymentOrder.patientName}</p>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <p className="text-sm text-blue-900">Order Total:</p>
                  <p className="font-medium text-blue-900">₹{(paymentOrder.total || 0).toFixed(2)}</p>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <p className="text-sm text-blue-900">Already Paid (Advance):</p>
                  <p className="font-medium text-blue-900">₹{(paymentOrder.advance || 0).toFixed(2)}</p>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-blue-200">
                  <p className="text-sm font-bold text-blue-900">Original Balance Due:</p>
                  <p className="text-lg font-bold text-red-600">₹{(paymentOrder.balance || 0).toFixed(2)}</p>
                </div>
              </div>
              
              <div className="space-y-5">
                {/* Additional Discount */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-1">Additional Discount on Delivery (₹)</label>
                  <p className="text-xs text-gray-500 mb-2">This will reduce the total order amount.</p>
                  <input
                    type="number"
                    value={additionalDiscount}
                    min="0"
                    max={paymentOrder.balance || 0}
                    step="0.01"
                    onChange={(e) => setAdditionalDiscount(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 bg-purple-50"
                    placeholder="Enter discount amount"
                    disabled={isProcessingPayment}
                  />
                </div>

                {/* Calculate Adjusted Balance */}
                {Number(additionalDiscount) > 0 && (
                   <div className="flex justify-between items-center bg-gray-50 p-3 rounded-lg border border-gray-200">
                     <span className="text-sm text-gray-700 font-medium">Adjusted Balance Due:</span>
                     <span className="text-base font-bold text-gray-900">
                       ₹{Math.max(0, (paymentOrder.balance || 0) - Number(additionalDiscount)).toFixed(2)}
                     </span>
                   </div>
                )}
                
                {/* Payment Amount */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-1">Payment Collected Now (₹)</label>
                  <p className="text-xs text-gray-500 mb-2">Enter the amount the patient is paying right now.</p>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={paymentAmount}
                      min="0"
                      max={Math.max(0, (paymentOrder.balance || 0) - (Number(additionalDiscount) || 0))}
                      step="0.01"
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-400 focus:border-green-400 bg-green-50"
                      placeholder="Enter payment amount"
                      disabled={isProcessingPayment}
                    />
                    <button
                      type="button"
                      onClick={() => setPaymentAmount(String(Math.max(0, (paymentOrder.balance || 0) - (Number(additionalDiscount) || 0))))}
                      disabled={isProcessingPayment}
                      className="px-4 py-2 bg-gray-200 text-gray-800 font-medium rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                    >
                      Pay Full
                    </button>
                  </div>
                </div>

                {/* Final Remaining Balance Display */}
                <div className={`p-4 rounded-lg border ${
                   Math.max(0, (paymentOrder.balance || 0) - (Number(additionalDiscount) || 0) - (Number(paymentAmount) || 0)) > 0 
                   ? 'bg-amber-50 border-amber-200' 
                   : 'bg-green-50 border-green-200'
                }`}>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-bold text-gray-800">Final Remaining Balance:</span>
                    <span className={`text-lg font-bold ${
                       Math.max(0, (paymentOrder.balance || 0) - (Number(additionalDiscount) || 0) - (Number(paymentAmount) || 0)) > 0 
                       ? 'text-amber-600' 
                       : 'text-green-600'
                    }`}>
                      ₹{Math.max(0, (paymentOrder.balance || 0) - (Number(additionalDiscount) || 0) - (Number(paymentAmount) || 0)).toFixed(2)}
                    </span>
                  </div>
                  {Math.max(0, (paymentOrder.balance || 0) - (Number(additionalDiscount) || 0) - (Number(paymentAmount) || 0)) > 0 && (
                     <p className="text-xs text-amber-700 mt-1 mt-2">
                       ⚠️ This order will automatically appear in the <strong>Reminders</strong> page to collect the remaining balance.
                     </p>
                  )}
                </div>
                
                {/* Delivery Description */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-1">Delivery Notes</label>
                  <textarea
                    value={deliveryDescription}
                    onChange={(e) => setDeliveryDescription(e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 resize-none"
                    placeholder="E.g., 'Delivered to home', 'Picked up by customer', etc."
                    rows="2"
                    disabled={isProcessingPayment}
                  />
                </div>
              </div>
            </div>
            
            <div className="p-5 border-t border-gray-100 bg-gray-50 rounded-b-xl flex gap-3">
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setPaymentOrder(null);
                  setPaymentAmount("");
                  setAdditionalDiscount("");
                  setDeliveryDescription("");
                }}
                disabled={isProcessingPayment}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium text-gray-700 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={processPayment}
                disabled={isProcessingPayment}
                className="flex-1 px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 shadow-md flex items-center justify-center gap-2"
              >
                {isProcessingPayment ? 'Processing...' : 'Confirm Delivery'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Edit Order Modal */}
      {editingOrder && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-800">Edit Order: {editingOrder.billNumber || '-'}</h3>
              <button onClick={() => setEditingOrder(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
                &times;
              </button>
            </div>
            
            <form onSubmit={handleEditSave} className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name</label>
                  <input type="text" value={editFormData.patientName} onChange={e => setEditFormData({...editFormData, patientName: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                  <input type="text" value={editFormData.mobile} onChange={e => setEditFormData({...editFormData, mobile: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                  <input type="text" value={editFormData.age} onChange={e => setEditFormData({...editFormData, age: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                  <select value={editFormData.gender} onChange={e => setEditFormData({...editFormData, gender: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none">
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input type="text" value={editFormData.address} onChange={e => setEditFormData({...editFormData, address: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
              </div>
              
              <h4 className="font-semibold text-gray-800 border-b pb-2 mb-4">Financial Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Bill Amount (₹)</label>
                  <input type="number" min="0" step="0.01" value={editFormData.total} onChange={e => setEditFormData({...editFormData, total: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Advance Paid (₹)</label>
                  <input type="number" min="0" step="0.01" value={editFormData.advance} onChange={e => setEditFormData({...editFormData, advance: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                <div className="md:col-span-2 bg-blue-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center text-blue-900">
                    <span className="font-medium">Calculated Balance Due:</span>
                    <span className="text-xl font-bold">₹{Math.max(0, Number(editFormData.total) - Number(editFormData.advance)).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-end gap-3 border-t pt-4">
                <button type="button" onClick={() => setEditingOrder(null)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium">Cancel</button>
                <button type="submit" disabled={isSavingEdit} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50">
                  {isSavingEdit ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrderingStatus;